import React, { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Fab, Grid, Tab, Tabs, Tooltip } from '@mui/material';
import { CustomerDetail } from '@pages/Customers/CustomerDetail/CustomerDetail';
import { CustomerAddressList } from '@pages/Customers/CustomerAddressList/CustomerAddressList';
import { OrderHistory } from '@pages/Customers/OrderHistory/OrderHistory';
import { CouponsView } from '@pages/Customers/Coupons/CouponsView';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { OrderSetupModal } from '@pages/Customers/OrderSetupModal/OrderSetupModal';
import { resetOrder, setShippingAddress } from '@store/slices/order';
import { showToast } from '@store/slices/notification';
import { useCheckBlackListStatusQuery } from '@services';
import FirebaseProviders from '../Providers/FirebaseProviders';
import { useCustomer } from '@hooks/useCustomer';
import { useLocation } from 'react-router-dom';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HomeIcon from '@mui/icons-material/Home';
import LiquorIcon from '@mui/icons-material/Liquor';
import DiscountIcon from '@mui/icons-material/Discount';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import { useGetCustomersQuery } from '@services';
import { setSelectedCustomer } from '@store/slices/customer';
import { Article } from '@mui/icons-material';
import { ActivityLogPage } from '@pages/Customers/ActivityLog/ActivityLog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type CustomerId = {
  id: string;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const getCustomerTabValue = (location: string) => (location ? Number(location?.split('#')[1]) : 0) ?? 0;

const CustomerDetailPage: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const activeUser = useAppSelector((state) => state.activeUser.value);
  const location = useLocation();
  const [customerTabValue, setCustomerTabValue] = useState(getCustomerTabValue(location?.hash));
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus.role > 1;
  const hasLogsViewAccess = userAccessStatus.role > 4;
  const { getAttribute } = useCustomer();

  const handleOnClose = () => {
    setIsModalOpen(false);
    dispatch(setShippingAddress(undefined));
  };

  const isPhoneVerified = getAttribute('phonenumberverifieddate');

  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const history = useHistory();

  useEffect(() => {
    const { completeOrderRedirect } = location.state || ({} as any);

    completeOrderRedirect === 'success' && setCustomerTabValue(2);
  }, [location.state]);

  const hasAddresses = customer?.address_count !== undefined && customer?.address_count > 0;

  useEffect(() => {
    const updatedCustomerTabValue = getCustomerTabValue(location?.hash);

    setCustomerTabValue(updatedCustomerTabValue);
  }, [location]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCustomerTabValue(newValue);

    history.push({
      hash: `${newValue}`,
    });
  };

  const setCustomerAddressTab = () => {
    setCustomerTabValue(1);
  };

  const { data } = useCheckBlackListStatusQuery(customer?.phone);

  const canViewOrderButton = hasWriteAccess;

  const orderButtonClickable = hasAddresses && typeof isPhoneVerified == 'string';

  const openOrderPage = () => {
    hasAddresses && setIsModalOpen(true);
    dispatch(resetOrder());
  };

  useEffect(() => {
    if (!customer) {
      if (history) {
        history.goBack();
      }
    }
  }, [customer, history]);

  const toolTipTitle = !hasAddresses ? 'The user must have an address to create an order.' : 'The user must have phone number verified';

  // Get the customer id passed in as params from the URL

  const params = useParams() as CustomerId;
  const customerUrlId = params.id;

  const { data: customerUrlData } = useGetCustomersQuery(
    {
      searchType: 'id',
      searchValue: customerUrlId,
      page: 1,
    },
    { skip: Number(customerUrlId) === customer.id }, // skip if the redux and url id are the same
  );

  if (customerUrlData) {
    const uriCustomer = customerUrlData.data.find((customer: any) => customer.id === Number(customerUrlId));

    if (uriCustomer) {
      dispatch(setSelectedCustomer(uriCustomer));
    } else {
      history.push('/customers');

      dispatch(
        showToast({
          message: `No customer found with id ${customerUrlId}`,
          type: 'error',
        }),
      );
    }
  }

  const renderOrderButton = () => {
    return (
      <Tooltip title={toolTipTitle} disableHoverListener={orderButtonClickable}>
        <Fab
          color="primary"
          variant="extended"
          sx={{
            position: 'fixed',
            bottom: '5vh',
            right: '16px',
            zIndex: '999',
            '&.Mui-disabled': {
              pointerEvents: 'auto',
              backgroundColor: 'rgba(0, 0, 0, 0.26)',
            },
          }}
          onClick={openOrderPage}
          data-testid="create-order-button"
          disabled={!orderButtonClickable}
          // Must be a div when the tooltip is active because a disabled button has no event listeners, which the tooltip needs
          component={orderButtonClickable ? 'div' : 'button'}
        >
          <AddCircleIcon sx={{ mr: 1 }} /> Create Order
        </Fab>
      </Tooltip>
    );
  };

  return (
    <Grid container spacing={1} data-testid="customer-detail-panel">
      {customer && (
        <Grid item flex={1}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs data-testid="Tabs" value={customerTabValue} onChange={handleChange}>
              <Tab icon={<ManageAccountsIcon />} role={'tab'} label="Customer Details" />
              <Tab icon={<HomeIcon />} label="Addresses" />
              <Tab icon={<LiquorIcon />} label="Order History" />
              <Tab icon={<DiscountIcon />} label="Coupons" />
              <Tab icon={<PermDataSettingIcon />} label="Firebase" />
              {hasLogsViewAccess && <Tab icon={<Article />} label="Logs" />}
            </Tabs>
          </Box>
          <TabPanel data-testid="customer-detail-tab" value={customerTabValue} index={0}>
            <CustomerDetail blacklistedData={data} />
          </TabPanel>
          <TabPanel data-testid="customer-address-tab" value={customerTabValue} index={1}>
            <CustomerAddressList />
          </TabPanel>
          <TabPanel data-testid="order-history-tab" value={customerTabValue} index={2}>
            <OrderHistory selectedCustomer={customer} />
          </TabPanel>
          <TabPanel data-testid="coupons-tab" value={customerTabValue} index={3}>
            <CouponsView selectedCustomer={customer} />
          </TabPanel>
          <TabPanel data-testid="firebase-tab" value={customerTabValue} index={4}>
            <FirebaseProviders selectedCustomer={customer} />
          </TabPanel>
          {hasLogsViewAccess && (
            <TabPanel data-testid="activity-logss-tab" value={customerTabValue} index={5}>
              <ActivityLogPage selectedCustomer={customer} />
            </TabPanel>
          )}
          {canViewOrderButton && renderOrderButton()}
          <OrderSetupModal
            data-testid="order-setup-modal"
            isOpen={isModalOpen}
            onClose={handleOnClose}
            addNewAddress={setCustomerAddressTab}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default CustomerDetailPage;
